import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border text-xs font-semibold transition-colors",
  {
    variants: {
      // SIZE EXISTS BECAUSE THE PRODUCT ALREADY VOTED, 2026-08-10.
      //
      // Badge shipped with exactly one size, px-3 py-0.5. Of the 28 hand-rolled
      // tinted TEXT pills in the tree, not one used that padding. They cluster at
      // px-2 py-0.5 (11) and px-1.5 py-0.5 (7), tailing off through px-2.5 py-1,
      // px-3 py-1 and one px-5 py-2. sm and xs are those two clusters, taken from
      // the measurement rather than invented.
      //
      // COUNT THE RIGHT THING. A first pass at this said "61 hand-rolled pills",
      // which was wrong and worth writing down because the error is instructive:
      // the regex matched `rounded-full` beside a role tint, and 33 of those 61
      // are h-8 w-8 icon CIRCLES, not badges at all. Grepping for a shape and
      // calling the result a component's substitutes is how you end up
      // "migrating" 33 things that were never badges.
      //
      // AND MOST OF THE REMAINING 28 ARE NOT BADGE SUBSTITUTES EITHER. 17 use
      // font-medium against this component's font-semibold, and 10 use a /15 tint
      // in light where this uses /10. That is a consistent, deliberately quieter
      // inline-annotation treatment — a tag inside a sentence — not a status chip
      // someone rebuilt badly. Do NOT bulk-convert them: it would make dozens of
      // screens louder for the sake of a tidy grep.
      //
      // What sm/xs are for is the genuine case: a status badge in a dense row that
      // previously had to be hand-rolled because the only size on offer was too
      // big. Those can migrate as they are touched.
      size: {
        // The default. For an h-5 (20px) badge the circular cap radius is 10px,
        // so px-2.5 (10px) puts glyphs right at the arc and reads as clipped.
        // px-3 (12px) buys 2px of daylight past the cap — the same principle the
        // Input and Button components landed on. Keep this for standalone badges
        // where the pill is the only thing in its row.
        default: "px-3 py-0.5",
        // Table rows and chip groups, where px-3 makes a row of four pills read
        // as a wall. The clipping concern above is weaker here because these sit
        // beside other content that establishes the baseline.
        sm: "px-2 py-0.5",
        // Inline within a sentence or a dense flow — a timeline marker, a status
        // dot with a word beside it. Seven of the measured hand-rolls landed
        // exactly here. (One other used py-px for a "+£40" delta chip; that is a
        // single site and not enough to justify a fourth step.)
        xs: "px-1.5 py-0.5",
      },
      variant: {
        default:     "border-primary/20 bg-primary/10 text-primary dark:border-primary/25 dark:bg-primary/15",
        secondary:   "border-transparent bg-secondary text-secondary-foreground",
        success:     "border-success/20 bg-success/10 text-success-ink dark:border-success/25 dark:bg-success/15",
        warning:     "border-warning/20 bg-warning/10 text-warning-ink dark:border-warning/25 dark:bg-warning/15",
        // text-danger-ink, not text-danger. The two resolve to the SAME colour in
        // both themes — --danger-ink exists so the rule "words wear -ink" can be
        // written once — so this is a no-op visually and a consistency fix in the
        // one place a reader checks what the rule is.
        danger:      "border-danger/20 bg-danger/10 text-danger-ink dark:border-danger/25 dark:bg-danger/15",
        // info was MISSING until 2026-08-10, despite being a first-class semantic
        // role with its own tokens. Its absence is visible in the tree: 33 places
        // hand-roll `bg-info/10`, and the four that are pill-shaped each picked
        // their own border opacity — /15, /20, /25, /40 — for one state.
        info:        "border-info/20 bg-info/10 text-info dark:border-info/25 dark:bg-info/15",
        outline:     "text-foreground border-border",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant, size }), className)} {...props} />;
}

export { Badge, badgeVariants };
